import { useState } from "react";
import { Container } from "../../components/atoms";

// ── Inline atoms (no external imports) ────────────────────────────────────────
const Btn = ({ children, onClick, className = "" }:any) => (
  <button onClick={onClick} className={`font-bold py-2 px-6 rounded-full cursor-pointer ${className}`}>
    {children}
  </button>
);

// ── Shared micro-components — exact Solveesy palette ─────────────────────────

const Tag = ({ children }:any) => (
  <span className="inline-block bg-[#EEF4FF] text-[#015CFD] text-[11px] font-bold px-3 py-1 rounded-full uppercase tracking-widest">
    {children}
  </span>
);

const SectionLabel = ({ children }:any) => (
  <p className="text-[11px] font-bold tracking-[2px] uppercase text-[#686868] mb-1">{children}</p>
);

const H2 = ({ children }:any) => (
  <h2 className="text-[#000000] font-bold text-[22px] md:text-[28px] mt-12 mb-4 pb-3 border-b-2 border-[#000000] leading-snug">
    {children}
  </h2>
);

const Body = ({ children, className = "" }:any) => (
  <p className={`text-[15px] text-[#464646] leading-[1.78] mb-5 font-normal ${className}`}>
    {children}
  </p>
);

const PullQuote = ({ children }:any) => (
  <div className="border-l-4 border-[#015CFD] bg-[#EEF4FF] rounded-r-lg px-6 py-5 my-8">
    <p className="text-[#015CFD] text-[16px] md:text-[18px] italic leading-[1.6] m-0 font-semibold">
      {children}
    </p>
  </div>
);

const ICard = ({ label, title, children, className = "" }:any) => (
  <div className={`my-8 rounded-lg border border-[#E3E4F1] bg-white py-6 px-6 md:px-8 ${className}`}>
    {label && <SectionLabel>{label}</SectionLabel>}
    {title && (
      <h3 className="text-[#000000] font-bold text-[18px] md:text-[22px] mb-6 leading-snug">
        {title}
      </h3>
    )}
    {children}
  </div>
);

// ── INFOGRAPHIC 1 — Executive Summary ────────────────────────────────────────
const ExecutiveSummary = () => (
  <ICard label="Executive Summary · 2026 White Paper">
    <p className="text-[15px] text-[#464646] leading-[1.78] font-normal mb-5">
      Debt recovery is no longer just about enforcement. Across the globe, the approach is shifting
      toward ethics, efficiency, and empathy. This whitepaper explores how major international
      markets — United Kingdom, United States, and Singapore — are reshaping debt recovery and
      compares their frameworks with India's evolving system.
    </p>
    <div className="border-t border-[#E3E4F1] pt-5">
      <p className="text-[12px] font-bold text-[#101010] mb-3 uppercase tracking-wide">Key Takeaways</p>
      <div className="flex flex-col gap-3">
        {[
          "Global jurisdictions prioritize borrower protection and tech-driven processes.",
          "India has improved significantly with regulatory reforms like IBC, but still lags in digital integration.",
          "The future of recovery lies in a hybrid model — merging structure, technology, and empathy.",
        ].map((t, i) => (
          <div key={i} className="flex gap-3 items-start">
            <div className="w-5 h-5 min-w-[20px] rounded-full bg-[#EEF4FF] flex items-center justify-center mt-0.5 flex-shrink-0">
              <span className="text-white text-[10px] font-bold">{i + 1}</span>
            </div>
            <p className="text-[14px] text-[#464646] leading-snug m-0 font-normal">{t}</p>
          </div>
        ))}
      </div>
    </div>
  </ICard>
);

// ── INFOGRAPHIC 2 — Global Recovery Models ───────────────────────────────────
const GlobalModels = () => {
  const countries = [
    {
      name: "United Kingdom",
      model: "Borrower Rehabilitation Model",
      body: "Financial Conduct Authority (FCA)",
      points: [
        "Strict enforcement of ethical recovery practices",
        "Proportionality and transparency are mandated",
        "Debt Relief Order (DRO): 12-month moratorium for debts under £50,000",
        "Post-moratorium debt written off if situation persists",
      ],
      implication: "Moratorium-based approach could reduce legal backlogs in India",
    },
    {
      name: "Singapore",
      model: "Tech-First Recovery Strategy",
      body: "Monetary Authority of Singapore (MAS)",
      points: [
        "AI-powered analytics to identify risky accounts early",
        "Blockchain platforms ensure transparency and reduce disputes",
        "Institutional collaboration enables real-time enforcement",
      ],
      implication: "NBFCs and fintechs must scale adoption of predictive analytics and digital contracts",
    },
    {
      name: "United States",
      model: "Regulatory Diversity & Innovation",
      body: "Fair Debt Collection Practices Act (FDCPA)",
      points: [
        "Consumer protection against abusive recovery tactics",
        "Debt validation required within 5 days of first contact",
        "ML-based customised repayment plans using income and spending data",
      ],
      implication: "Encourages wider experimentation with AI in collections and borrower-centric models",
    },
  ];

  return (
    <ICard label="Global Recovery Models" title="How UK, US & Singapore Are Leading">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {countries.map((c, i) => (
          <div key={i} className="flex flex-col border border-[#E3E4F1] rounded-lg overflow-hidden">
            <div className=" bg-[#EEF4FF] px-4 py-3">
              <div className="text-white font-bold text-[15px]">{c.name}</div>
              <div className="text-white/70 text-[11px] font-semibold uppercase tracking-wide mt-0.5">{c.model}</div>
            </div>
            <div className="bg-[#F6F6F6] px-4 py-4 flex-1">
              <div className="text-[11px] font-bold text-[#686868] uppercase tracking-wide mb-1">Regulatory Body</div>
              <div className="text-[12px] text-[#464646] font-semibold mb-3">{c.body}</div>
              <div className="flex flex-col gap-2">
                {c.points.map((p, j) => (
                  <div key={j} className="flex gap-2 items-start">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#015CFD] flex-shrink-0 mt-[6px]" />
                    <span className="text-[12.5px] text-[#464646] leading-snug">{p}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-[#EEF4FF] px-4 py-3 border-t border-[#015CFD]/20">
              <div className="text-[10px] font-bold text-[#015CFD] uppercase tracking-widest mb-1">Implication for India</div>
              <div className="text-[12px] text-[#464646] leading-snug">{c.implication}</div>
            </div>
          </div>
        ))}
      </div>
    </ICard>
  );
};

// ── INFOGRAPHIC 3 — India Metrics ────────────────────────────────────────────
const IndiaMetrics = () => (
  <ICard label="India Performance · Recovery Metrics">
    <div className="grid grid-cols-2 gap-4 mb-6">
      {[
        { stat: "71.6 Cents", label: "Recovery per dollar of debt", sub: "vs. OECD average: 70.2" },
        { stat: "1.6 Yrs", label: "Average resolution time", sub: "Comparable to global practices" },
      ].map((m, i) => (
        <div key={i} className=" bg-[#EEF4FF] rounded-xl px-4 py-6 text-center">
          <div className="text-white font-bold text-[28px] md:text-[40px] leading-none mb-2">{m.stat}</div>
          <div className="text-white/90 text-[13px] font-semibold">{m.label}</div>
          <div className="text-white/60 text-[11px] mt-1">{m.sub}</div>
        </div>
      ))}
    </div>
    <div className="flex flex-col gap-3">
      {[
        { icon: "⚙️", title: "Manual recovery dominates", desc: "NBFC and SME sectors still rely heavily on human-led, unstructured processes" },
        { icon: "💻", title: "Limited tech adoption", desc: "AI, blockchain and real-time data analysis remain underutilised compared to Singapore and the US" },
      ].map((g, i) => (
        <div key={i} className="flex gap-3 items-start bg-[#F6F6F6] rounded-lg px-4 py-3">
          <span style={{ fontSize: 22 }}>{g.icon}</span>
          <div>
            <div className="text-[14px] font-semibold text-[#101010]">{g.title}</div>
            <div className="text-[13px] text-[#686868] font-normal leading-snug mt-0.5">{g.desc}</div>
          </div>
        </div>
      ))}
    </div>
  </ICard>
);

// ── INFOGRAPHIC 4 — Comparison Table ─────────────────────────────────────────
type Row = {
  criteria: string;
  india: string;
  uk: string;
  sg: string;
  us: string;
  hi: string[];
};

const ComparisonTable = () => {
  const rows: Row[] = [
    { criteria: "Ethics & Regulation", india: "RBI Fair Practices Code", uk: "FCA strict compliance", sg: "MAS oversight", us: "FDCPA + state-level rules", hi: [] },
    { criteria: "Tech Adoption", india: "Low to Medium", uk: "Moderate", sg: "High", us: "High", hi: ["sg", "us"] },
    { criteria: "Borrower Protections", india: "Internal grievance redressal", uk: "DRO-based write-offs", sg: "Debt tracking transparency", us: "Strong consumer rights", hi: [] },
    { criteria: "Innovation in Recovery", india: "Mediation & Arbitration", uk: "Risk-based recovery", sg: "AI & Blockchain", us: "ML-based repayment", hi: ["sg", "us"] },
    { criteria: "Avg. Resolution Time", india: "1.6 years", uk: "~1 year", sg: "~1 year", us: "1–1.5 years", hi: [] },
  ];

  type DataKey = Exclude<keyof Row, "hi">;

  const headers = ["Criteria", "India", "UK", "Singapore", "USA"];
  const keys: DataKey[] = ["criteria", "india", "uk", "sg", "us"];

  return (
    <ICard label="Cross-Country Comparison · 5 Criteria" title="India | UK | Singapore | USA">
      <div className="overflow-x-auto -mx-2">
        <table className="w-full text-[13px] border-collapse min-w-[500px]">
          <thead>
            <tr>
              {headers.map((h, i) => (
                <th key={i} className={`text-left px-3 py-2 text-[11px] font-bold uppercase tracking-wide border-b-2 border-[#E3E4F1] whitespace-nowrap ${i === 0 ? "text-[#686868]" : "text-[#015CFD]"}`}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-[#F6F6F6]"}>
                {keys.map((k, j) => (
                  <td
                    key={j}
                    className={`px-3 py-2.5 border-b border-[#E3E4F1] leading-snug align-top ${
                      k === "criteria"
                        ? "font-semibold text-[#101010]"
                        : row.hi.includes(k)
                        ? "font-bold text-[#015CFD]"
                        : "text-[#464646] font-normal"
                    }`}
                  >
                    {row[k]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </ICard>
  );
};

// ── INFOGRAPHIC 5 — Strategic Recommendations ────────────────────────────────
const StrategicRecs = () => {
  const recs = [
    { num: "1", title: "Accelerate tech adoption", desc: "Encourage AI and digital contracts for NBFCs and fintechs." },
    { num: "2", title: "Enhance borrower profiling", desc: "Move from asset-based to behavior-based recovery." },
    { num: "3", title: "Collaborative platforms", desc: "Use blockchain for multi-party enforcement visibility." },
    { num: "4", title: "Empathy-driven recovery", desc: "Incorporate socio-economic insights into collection strategy." },
  ];
  return (
    <ICard label="Strategic Recommendations · India" title="Four Shifts to Close the Gap">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {recs.map((r, i) => (
          <div key={i} className="flex gap-4 items-start bg-[#EEF4FF] border border-[#015CFD]/20 rounded-xl px-5 py-4">
            <div className="text-[28px] font-bold text-[#015CFD] leading-none min-w-[20px] flex-shrink-0">{r.num}.</div>
            <div>
              <div className="text-[14px] font-semibold text-[#101010] mb-1">{r.title}</div>
              <div className="text-[13px] text-[#686868] font-normal leading-snug">{r.desc}</div>
            </div>
          </div>
        ))}
      </div>
    </ICard>
  );
};

// ── INFOGRAPHIC 6 — Four Ways Solveesy Bridges the Gap ───────────────────────
// Exact pink→purple gradient card from Contact.tsx / Datadriven.tsx
const SolveesyBridges = () => {
  const items = [
    { icon: "🤖", title: "AI-Driven Risk Sharing", desc: "Predictive models identify stress signals early — enabling intervention at 30–60 DPD, not 365+" },
    { icon: "📡", title: "Data-Enriched Communication", desc: "Recovery outreach informed by real borrower data — not generic scripts" },
    { icon: "🤝", title: "Collaborative Negotiation Tools", desc: "Structured repayment modelling that fosters compliance rather than confrontation" },
    { icon: "🔍", title: "Tech-Enabled Grievance Redressal", desc: "Full audit trails and conduct standards — built for RBI inspection readiness" },
  ];
  return (
    <div className="my-8 rounded-[40px] overflow-hidden" style={{ background: "linear-gradient(136deg,#FF9898 14.73%,#8054FF 86.33%)" }}>
      <div className="px-6 md:px-10 py-8 md:py-10">
        <p className="text-[11px] font-bold tracking-[2px] uppercase mb-1" style={{ color: "rgba(255,255,255,0.6)" }}>
          Solveesy · Collections as a Service
        </p>
        <h3 className="text-white font-bold text-[20px] md:text-[26px] mb-6 leading-snug">
          Four Ways Solveesy Bridges the Gap
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          {items.map((item, i) => (
            <div key={i} className="rounded-xl p-5" style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)" }}>
              <div className="mb-2" style={{ fontSize: 24 }}>{item.icon}</div>
              <div className="text-white font-semibold text-[14px] mb-1">{item.title}</div>
              <div className="text-[13px] leading-snug font-normal" style={{ color: "rgba(255,255,255,0.7)" }}>{item.desc}</div>
            </div>
          ))}
        </div>
        <div className="rounded-xl px-5 py-4 text-center" style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.25)" }}>
          <p className="text-[15px] italic font-semibold leading-snug m-0" style={{ color: "rgba(255,255,255,0.92)" }}>
            "Solveesy is proud to lead this evolution — powering a smarter, faster, and fairer recovery ecosystem"
          </p>
        </div>
      </div>
    </div>
  );
};

// ── MAIN PAGE ─────────────────────────────────────────────────────────────────
export default function SolveesyWhitePaper() {
  return (
    <div className="bg-white min-h-screen font-sans">

      {/* ── HERO — white bg, exact Solveesy hero layout from previous blog ── */}
      <div className="relative overflow-hidden">
        {/* Same #00C2FF blur-[200px] opacity-25 glow circle from every Solveesy page */}
        <div
          className="absolute pointer-events-none"
          style={{
            width: 403, height: 403, borderRadius: "50%",
            background: "#00C2FF", filter: "blur(200px)",
            opacity: 0.25, top: -80, left: 60, zIndex: 0,
          }}
        />

  <Container className="py-14 md:py-20">
           <div className="flex flex-col md:flex-row justify-between items-center gap-10">
 
             {/* Left */}
             <div className="w-full md:w-full flex flex-col items-center md:items-start text-center md:text-left">
               <div className="flex flex-wrap gap-2 justify-center md:justify-start mb-4">
                 <Tag>Comparative Debt Recovery Mechanism</Tag>      
               </div>
 
               <h1 className="text-[28px] md:text-[42px] font-bold leading-tight text-[#000000] mb-3">
                 International Debt Recovery Mechanisms and
                 <br/>
                 <span className="text-[#015CFD] md:text-[30px]">India's Emerging Framework</span>
               </h1>
 
               <p className="text-[#464646] text-sm md:text-base font-normal w-full md:w-4/5 leading-relaxed mb-6">
                How UK, US, and Singapore are reshaping debt recovery — and what India's evolving system
can learn from them
               </p>
             </div>
           </div>
         </Container>
      </div>

      {/* ── ARTICLE BODY ── */}
 <Container className="py-12 md:py-16">
        <ExecutiveSummary />

        {/* Section 1 */}
        <H2>Global Recovery Models</H2>
        <Body>
          Three major global markets have developed distinct but complementary approaches to debt recovery — each offering India a lens through which to evaluate its own framework and identify areas of meaningful modernisation.
        </Body>
        <GlobalModels />

        <H2>India: Strengths and Shortcomings</H2>
        <Body>
          India's regulatory backbone has strengthened considerably — but execution gaps remain, particularly in smaller NBFC and SME lending segments where manual processes still dominate.
        </Body>
        <Body>
          <strong className="text-[#000000]">Regulatory backbone:</strong> RBI's Fair Practices Code prohibits coercion and mandates trained recovery personnel. The Insolvency and Bankruptcy Code (IBC), 2016 streamlined corporate recovery, while the introduction of arbitration and mediation has helped avoid protracted litigation.
        </Body>
        <IndiaMetrics />

        {/* <PullQuote>
          India is no longer behind in the recovery game — but there is room to grow. What lags is structural modernisation: the shift from manual processes to AI-assisted, empathy-informed, tech-enabled recovery infrastructure.
        </PullQuote> */}

        {/* Section 3 */}
        <H2>Cross-Country Comparison</H2>
        <Body>
          Benchmarking India's performance across five key criteria reveals a consistent pattern: while regulatory intent is sound, execution in technology adoption and borrower innovation still lags behind Singapore and the US.
        </Body>
        <ComparisonTable />

        {/* Section 4 */}
        <H2>Strategic Recommendations for India</H2>
        <Body>
          India's path forward is clear — the ingredients exist, but they need to be assembled at scale. The following four shifts would meaningfully close the gap with global leaders.
        </Body>
        <StrategicRecs />

       

        {/* Section 5 */}
        <H2>How Solveesy Leads the Change</H2>
        <Body>
          Solveesy blends the best of global practices with India's ground realities — not as a theoretical model, but as a working platform deployed with lenders across consumer finance, digital lending, and MSME credit.
        </Body>
        <Body>
          A shift toward technology, combined with ethical and empathetic enforcement, will define the next phase of growth.
        </Body>
        <SolveesyBridges />

        {/* Conclusion */}
        <H2>Conclusion</H2>
        <Body>
          India is no longer behind in the recovery game — but there is room to grow. The data shows competitive resolution rates and timelines. What lags is the structural modernisation: the shift from manual processes to AI-assisted, empathy-informed, tech-enabled recovery infrastructure.
        </Body>
        <Body>
          A shift toward technology, combined with ethical and empathetic enforcement, will define the next phase of growth. The global frameworks explored here are not aspirational templates — they are operational realities that Indian lenders can begin adopting today.
        </Body>

        {/* ── CTA — exact gradient card from Contact.tsx / Datadriven.tsx ── */}
        <div
          className="mt-14 overflow-hidden"
          style={{ background: "linear-gradient(136deg,#FF9898 14.73%,#8054FF 86.33%)", borderRadius: 40 }}
        >
          <div className="flex flex-col items-center justify-center text-center px-6 md:px-16 py-10 md:py-14">
            <h2 className="text-white font-bold text-[24px] md:text-[36px] leading-tight mb-3">
              Ready to modernise your collections strategy?
            </h2>
            <p className="text-[15px] md:text-[18px] leading-relaxed max-w-lg mb-8 font-normal" style={{ color: "rgba(255,255,255,0.8)" }}>
              Solveesy works with lenders across consumer finance, digital lending, and MSME credit to build compliant, early-stage collections capability.
            </p>
        
          </div>
        </div>


     </Container>
    </div>
  );
}